import React, { useEffect } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { EVENT_NAMES } from "@scrile/api-provider/dist";
import MainRouterView from "router/MainRouterView";
import RouteController from "router/RouteController";
import { AppStateContextProvider } from "hooks/useAppState";
import useAuthUser, { AuthUserContextProvider } from "hooks/useAuthUser";
import { AuthUserVerificationContextProvider } from "hooks/useAuthUserVerificationStatus";
import useCustomDomain, { CustomDomainContextProvider } from "hooks/useCustomDomain";
import { PerformersContextProvider } from "hooks/usePerformers";
import { getLoginData } from "lib/authHelper";
import emitter from "lib/emitter";
import { getAuthToken } from "lib/providers";
import ModalBalanceRefill from "modals/ModalBalanceRefill";
import AppointmentBanner from "components/AppointmentBanner";
import CalendlyEventListener from "components/CalendlyEventListener";
import DesktopUpdateBanner from "components/DesktopUpdateBanner";
import IncomingCallModal from "components/IncomingCallModal";
import PageFooter from "components/PageFooter";
import PageHeader from "components/PageHeader";
import PushManager from "components/PushManager";
import ShareTargetModal from "components/ShareTargetModal";
import useWebPush from "hooks/useWebPush";
import useNativePush from "hooks/useNativePush";
import useNativeCallKit from "hooks/useNativeCallKit";
import useMobileAppLifecycle from "hooks/useMobileAppLifecycle";
import useShareTarget from "hooks/useShareTarget";
import useDesktopPush from "hooks/useDesktopPush";
import useDesktopIncomingCall from "hooks/useDesktopIncomingCall";
import useViewportHeight from "hooks/useViewportHeight";
import "./styles/App.scss";

function WebPushSubscriber() {
  useWebPush();
  useNativePush();
  useNativeCallKit();
  useMobileAppLifecycle();
  useShareTarget();
  useDesktopPush();
  useDesktopIncomingCall();
  return null;
}

function App() {
  const { setLoginData } = useAuthUser();
  const { loadData, isLoading } = useCustomDomain();
  useViewportHeight();

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      document.body.classList.add("is-native");
      const platform = Capacitor.getPlatform();
      if (platform === "android") document.body.classList.add("platform-android");
      if (platform === "ios") document.body.classList.add("platform-ios");
    }
  }, []);

  useEffect(() => {
    const onEvent = (data: any) => {
      const oldData = getLoginData();
      if (JSON.stringify(data) === JSON.stringify(oldData)) return;
      setLoginData(data);
    };
    emitter.on(EVENT_NAMES.SET_USER, onEvent);
    return () => emitter.off(EVENT_NAMES.SET_USER, onEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync auth token → SESSIONID cookie so browser-native requests
  // (img src, video src, a href) to /graphql/files can authenticate.
  useEffect(() => {
    let cancelled = false;
    const syncCookie = async () => {
      const token = await getAuthToken();
      if (cancelled) return;
      if (token) {
        document.cookie = `SESSIONID=${token}; path=/; SameSite=Strict${
          window.location.protocol === "https:" ? "; Secure" : ""
        }`;
        // Sync to Electron session for cross-origin media requests (recordings, files)
        if (window.smeet?.session) {
          window.smeet.session.setCookie(token);
        }
      }
    };
    // Set immediately, on every auth event, and refresh every 30s
    syncCookie();
    emitter.on(EVENT_NAMES.SET_USER, syncCookie);
    const interval = setInterval(syncCookie, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
      emitter.off(EVENT_NAMES.SET_USER, syncCookie);
    };
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (isLoading) return null;

  return (
    <>
      <DesktopUpdateBanner />
      <PageHeader />
      <AppointmentBanner />
      <main className="main">
        <div className="container">
          <MainRouterView />
        </div>
      </main>
      <PageFooter />
      <RouteController />
      <PushManager />
      <WebPushSubscriber />
      <IncomingCallModal />
      <ShareTargetModal />
    </>
  );
}

export function WrappedApp() {
  return (
    <AuthUserContextProvider>
      <AuthUserVerificationContextProvider>
        <AppStateContextProvider>
          <CustomDomainContextProvider>
            <PerformersContextProvider>
              <App />
              <ModalBalanceRefill />
              <CalendlyEventListener />
            </PerformersContextProvider>
          </CustomDomainContextProvider>
        </AppStateContextProvider>
      </AuthUserVerificationContextProvider>
    </AuthUserContextProvider>
  );
}

export default function BrowserApp() {
  return (
    <Router>
      <WrappedApp />
    </Router>
  );
}
